/**
 * API response-projection contracts: the shapes the API *returns* and the
 * non-entity request bodies it accepts, as distinct from the entity and
 * create/update schemas in `entities.ts`. Both apps/api and apps/web import
 * these from `@paradise/shared` and never a subpath.
 *
 * Types are derived via `z.infer` — never hand-write a type that duplicates a
 * schema. See docs/plan.md for the endpoints these mirror.
 */
import { z } from 'zod';
import { Id, Product, ProductPhoto, Lead, LeadItem, LeadEvent } from './entities.js';
import { LeadStage } from './enums.js';
import { MoneyAmount } from './money.js';
import { QuotationSummary, QuoteRequestSummary, SelectionSummary } from './selections.js';
import { ShortlistLine } from './shortlist.js';
// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------
/**
 * One file in the eager-precache manifest. The pipeline writes TWO files per
 * photo — an ~800px `cover` and a ~240px `thumb` — so the manifest is a flat
 * list of files, not of photos, and cache completeness is
 * `cache.keys().length === assets.length`. See docs/plan.md, Eager catalogue
 * precache.
 */
export const ManifestAsset = z.object({
    path: z.string().min(1),
    bytes: z.number().int().nonnegative(),
    kind: z.enum(['thumb', 'cover']),
    itemNo: z.number().int().positive()
});
export const CatalogueManifest = z.object({
    // A content/version marker so a changed catalogue is a new manifest; the
    // precache diff is a set difference over `assets[].path` regardless.
    version: z.string().min(1),
    assets: z.array(ManifestAsset)
});
/**
 * The cheap freshness probe behind `/api/catalogue/version`. Deliberately NOT
 * `CatalogueManifest.version`, which hashes photo asset paths only: renaming a
 * product, editing a price or adding a photo-less one leaves that hash
 * identical while the text a device renders is now wrong. This hash covers the
 * text payload itself, so it moves whenever `/api/catalogue/products` would.
 */
export const CatalogueVersion = z.object({
    version: z.string().min(1)
});
/**
 * A product as the catalogue text endpoint serves it: the row plus its ordered
 * gallery (sortOrder 0 is the cover). Category and material NAMES are resolved
 * client-side by joining against the `/categories` and `/materials` lists the
 * client also fetches — the product stays id-only on the wire.
 */
export const ProductWithPhotos = Product.extend({
    photos: z.array(ProductPhoto)
});
// ---------------------------------------------------------------------------
// Leads
// ---------------------------------------------------------------------------
/**
 * A lead as the board/list endpoint serves it: the row plus its quoted lines,
 * so the board can render item counts and totals and the detail view works
 * offline from the mirror. Excludes archived leads at the query, not the schema.
 */
export const LeadWithItems = Lead.extend({
    items: z.array(LeadItem),
    /**
     * What this buyer currently wants: the meeting lines UNIONED with what they
     * ticked on a catalogue link. Derived server-side by `deriveShortlist`, never
     * stored, so deleting a quote request removes its lines with no un-apply.
     *
     * `items` beside this stays the raw meeting capture, because the edit form
     * owns exactly that set and submits it whole.
     */
    shortlist: z.array(ShortlistLine),
    /**
     * What this lead is worth according to the quotations actually sent, derived
     * server-side by `leadQuotedValue`. Null when none has been raised, and the
     * board then falls back to summing the shortlist — the conversation so far.
     *
     * On the wire rather than computed client-side because the board reads the
     * offline mirror, which carries `items` but has never carried quotations.
     */
    quotedTotal: MoneyAmount.nullable(),
    quotedBasis: z.enum(['accepted', 'sent']).nullable()
});
/**
 * A lead as the detail endpoint serves it: with items and the full
 * stage-history timeline. Newest-first is a rendering concern, not enforced
 * here.
 */
export const LeadDetail = LeadWithItems.extend({
    history: z.array(LeadEvent),
    // Newest first — resolved through Lead -> selections -> quoteRequests, so
    // an inactive/expired Selection's requests still surface here.
    quoteRequests: z.array(QuoteRequestSummary),
    /** Newest first. Includes revoked ones — the lead screen shows what was sent, not only what is live. */
    selections: z.array(SelectionSummary),
    quotations: z.array(QuotationSummary)
});
/**
 * The one-field stage move that also appends a `LeadEvent`. Requires
 * connectivity (see docs/plan.md — it is disabled offline). Tapping the
 * already-active stage is a client-side no-op, so the server need not special
 * case it.
 */
export const StageChangeInput = z.object({
    stage: LeadStage,
    note: z.string().nullable().optional()
});
/**
 * A timeline entry that records something happening WITHOUT claiming the
 * stage moved. Before this, "called, no answer" could only be logged by
 * re-picking a stage, and `POST /leads/:id/stage` treats the current stage as
 * a no-op — so the only way to make the note stick was to move the lead
 * somewhere it had not gone. The resulting `LeadEvent` has a null `stage`.
 */
export const AppendNoteInput = z.object({
    note: z.string().trim().min(1).max(2000)
});
/**
 * Setting or clearing the follow-up date on its own. `PATCH /api/leads/:id`
 * can do it too, but that route is multipart field-replacement built around
 * the card photo — wrong shape for a one-tap "push to next week" from the
 * queue, which is where most follow-up dates will actually get set.
 */
export const SetFollowUpInput = z.object({
    followUpDate: z.iso.date().nullable()
});
// ---------------------------------------------------------------------------
// Buyer directory — deduplicated projection over ALL leads
// ---------------------------------------------------------------------------
/**
 * One entry in the deduplicated buyer directory: a projection over every lead
 * (including archived), across every show and every user, so repeat-buyer
 * autofill recognises someone met at a different show on a different phone.
 * Precached; downloads first. See docs/plan.md, "The buyer directory has to be
 * precached too". Fields are lenient strings — this is a display projection,
 * not an entity.
 */
export const BuyerDirectoryEntry = z.object({
    company: z.string().min(1),
    contactName: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    city: z.string().nullable(),
    country: z.string().nullable(),
    lastSeenAt: z.iso.datetime()
});
// ---------------------------------------------------------------------------
// Event tagging
// ---------------------------------------------------------------------------
/**
 * The set of products tagged to an event, for catalogue tag mode and the
 * picker's default event scope. A plain id list — the client already holds the
 * products.
 */
export const TaggedProductIds = z.object({
    eventId: Id,
    productIds: z.array(Id)
});
/**
 * Every event↔product tag pair across all events — the bulk feed the offline
 * catalogue mirror precaches so "filter the browse list by event" works with no
 * signal (a per-event fetch would not survive a dead zone). Full-replaced into
 * the Dexie `eventTags` table on each pull.
 */
export const AllEventTags = z.object({
    tags: z.array(z.object({ eventId: Id, productId: Id }))
});
/**
 * Multipart field names for a lead mutation. The lead JSON travels in
 * `payload`; the visiting-card images in `cardFront` / `cardBack`. Shared so
 * the API parser, the online edit path and the offline sync-engine replay all
 * agree on the exact strings (they must, or a card silently never arrives).
 */
export const LEAD_PAYLOAD_FIELD = 'payload';
export const CARD_FRONT_FIELD = 'cardFront';
export const CARD_BACK_FIELD = 'cardBack';
//# sourceMappingURL=api.js.map